import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileEdit, User2, Trophy, ClipboardCheck, Lightbulb, BookOpen,
  ChevronLeft, ChevronRight, X, ClipboardList,
} from "lucide-react";

const DONE_KEY = (uid) => `kwgl_tour_done_${uid || "anon"}`;
export const TOUR_EVENT = "kwgl:open-tour";

const STEPS = [
  {
    to: "/", icon: LayoutDashboard, title: "Dashboard",
    when: "Sempre que entras",
    lines: ["É o teu ponto de situação: a semana em que estamos, a fase da edição e os totais de todos.",
            "Se houver prazo a fechar ou aviso do teu secretário, aparece aqui primeiro."],
    roles: ["admin", "coach", "consultant", "staff"],
  },
  {
    to: "/self-report", icon: FileEdit, title: "Reportar Atividade",
    when: "Todas as semanas, até domingo às 23:59",
    lines: ["O único passo obrigatório: contactos, reuniões, angariações, vendas e growth shares.",
            "Leva dois minutos e é daqui que saem os teus pontos e os do teu clube."],
    roles: ["consultant"],
  },
  {
    to: "/weekly-entry", icon: ClipboardList, title: "Registo Semanal",
    when: "Todas as semanas",
    lines: ["Lançamento e revisão dos registos semanais de qualquer consultor.",
            "É daqui que saem os pontos, os MVPs e os rankings da semana."],
    roles: ["admin"],
  },
  {
    to: "/my-profile", icon: User2, title: "O Meu Perfil",
    when: "Uma vez por semana, com calma",
    lines: ["Os teus objetivos, a tua sequência de semanas reportadas e o teu Legado.",
            "É aqui que vês o que fica de ti para além desta edição."],
    roles: ["consultant", "coach", "staff"],
  },
  {
    to: "/rankings", icon: Trophy, title: "Rankings",
    when: "Quando quiseres saber onde estás",
    lines: ["Consultores, clubes MBTI, equipas e secretários — e o Accountability, que premia quem não falha.",
            "Só as 10 semanas de coopetição contam para o campeonato."],
    roles: ["admin", "coach", "consultant", "staff"],
  },
  {
    to: "/roster", icon: ClipboardCheck, title: "Plantel da Liga",
    when: "Para saber quem está em campo",
    lines: ["Os 8 concorrentes de cada clube, mais os cartões atribuídos.",
            "Quem não foi escolhido continua Observador — e continua a contar para si."],
    roles: ["admin", "coach", "consultant", "staff"],
  },
  {
    to: "/eureka", icon: Lightbulb, title: "EUREKA",
    when: "Sempre que tiveres uma ideia",
    lines: ["Aberto a toda a KW Lead: propõe melhorias, vota nas dos outros, acompanha o que foi implementado.",
            "Uma ideia implementada vale um cartão branco — 80 pontos para o teu clube."],
    roles: ["admin", "coach", "consultant", "staff"],
  },
  {
    to: "/rules", icon: BookOpen, title: "Regras",
    when: "Lê uma vez, consulta quando precisares",
    lines: ["Quanto vale cada coisa, os bónus, e o que significam os cartões amarelo, vermelho e branco.",
            "Em caso de dúvida, é aqui que está a resposta."],
    roles: ["admin", "coach", "consultant", "staff"],
  },
];

export default function WelcomeTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const steps = STEPS.filter((s) => s.roles.includes(user?.role || "consultant"));

  const close = useCallback((markDone = true) => {
    setOpen(false);
    setI(0);
    if (markDone) {
      try { localStorage.setItem(DONE_KEY(user?.id), "1"); } catch (e) { /* storage blocked */ }
    }
  }, [user?.id]);

  // Abre automaticamente na primeira visita e sempre que pedido pelo menu de conta.
  useEffect(() => {
    if (!user?.id) return;
    const onOpen = () => { setI(0); setOpen(true); };
    window.addEventListener(TOUR_EVENT, onOpen);
    let done = true;
    try { done = localStorage.getItem(DONE_KEY(user.id)) === "1"; } catch (e) { done = true; }
    if (!done) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => { clearTimeout(t); window.removeEventListener(TOUR_EVENT, onOpen); };
    }
    return () => window.removeEventListener(TOUR_EVENT, onOpen);
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setI((v) => Math.min(steps.length - 1, v + 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, steps.length, close]);

  if (!open || steps.length === 0) return null;
  const s = steps[Math.min(i, steps.length - 1)];
  const last = i === steps.length - 1;

  return createPortal((
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="welcome-tour">
      <div className="w-full max-w-lg kw-card p-7 relative kw-fade-up">
        <button onClick={() => close()} className="absolute top-4 right-4 text-[#737373] hover:text-white"
          data-testid="tour-close"><X className="w-4 h-4" /></button>

        <div className="kw-overline mb-4">Guia de boas-vindas · 1 minuto</div>

        <div className="flex items-start gap-4">
          <span className="w-11 h-11 shrink-0 border border-[#E50000] bg-[#E50000]/10 flex items-center justify-center">
            <s.icon className="w-5 h-5 text-[#E50000]" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-3xl font-black uppercase tracking-tight leading-none"
              data-testid="tour-step-title">{s.title}</h2>
            <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-2">{s.when}</div>
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {s.lines.map((l) => (
            <p key={l} className="text-sm text-[#A3A3A3] leading-relaxed">{l}</p>
          ))}
        </div>

        <div className="flex gap-1.5 mt-6" data-testid="tour-progress">
          {steps.map((st, idx) => (
            <button key={st.to} onClick={() => setI(idx)} aria-label={st.title}
              className="h-1 flex-1 transition-colors"
              style={{ background: idx <= i ? "#E50000" : "#262626" }} />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-6">
          <div className="flex items-center gap-2">
            <button onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0}
              className="kw-btn-secondary !px-3 !py-2 disabled:opacity-30" data-testid="tour-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[10px] uppercase tracking-widest text-[#737373] tabular-nums">
              {i + 1}/{steps.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => { close(); navigate(s.to); }} className="kw-btn-secondary !py-2 text-[11px]"
              data-testid="tour-open-page">Abrir página</button>
            {last ? (
              <button onClick={() => close()} className="kw-btn-primary !py-2" data-testid="tour-finish">
                Começar
              </button>
            ) : (
              <button onClick={() => setI((v) => v + 1)} className="kw-btn-primary !py-2 inline-flex items-center gap-2"
                data-testid="tour-next">
                Seguinte <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
